import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SEO, { schemas } from '../components/SEO';
import './InnerPage.css';
import './FeaturesPage.css';

const features = [
  {
    icon: '/icons/features/features_19_quality-scoring.svg',
    id: 'quality',
    title: 'AI Response Quality Scoring',
    subtitle: 'Multi-method evaluation for every dimension',
    text: 'Score AI responses across accuracy, hallucination detection, relevance, and completeness. Choose from algorithmic methods (ROUGE, BLEU, Semantic Similarity) or LLM-as-Judge evaluation — or combine them for the highest confidence results.',
    bullets: [
      'ROUGE-1, ROUGE-2, ROUGE-L metrics',
      'BLEU score computation',
      'Cosine semantic similarity',
      'LLM-as-Judge configurable prompts',
      'Composite quality index scoring',
    ],
    tag: 'Core Evaluation',
  },
  {
    icon: '/icons/features/features_20_security-vuln.svg',
    id: 'security',
    title: 'Security Vulnerability Testing',
    subtitle: '122+ test cases across 14 attack vectors',
    text: 'Test your AI system against the complete spectrum of LLM security threats. From direct prompt injection to sophisticated multi-turn jailbreaks, AccuGen covers every attack category defined by OWASP and the broader AI security community.',
    bullets: [
      'Prompt injection (direct & indirect)',
      'Jailbreaking & instruction override',
      'Data leakage & exfiltration attempts',
      'RAG pipeline poisoning attacks',
      'Adversarial formatting & encoding',
      'Social engineering patterns',
    ],
    tag: 'Security',
  },
  {
    icon: '/icons/features/features_21_bias-fairness.svg',
    id: 'bias',
    title: 'Bias & Fairness Detection',
    subtitle: '12 dimensions of fairness evaluated simultaneously',
    text: 'Multi-prompt comparative evaluation detects subtle bias that single-prompt testing misses. Each bias dimension runs with statistically controlled variants to identify disparate treatment before it reaches users or regulators.',
    bullets: [
      'Gender & pronoun bias',
      'Racial & ethnic disparities',
      'Religious content fairness',
      'Age & seniority bias',
      'Disability representation',
      'Geographic & socioeconomic bias',
      'Cognitive & framing bias',
      'Tone consistency across groups',
    ],
    tag: 'Fairness',
  },
  {
    icon: '/icons/features/features_22_owasp-mapping.svg',
    id: 'owasp',
    title: 'OWASP LLM Top 10 Mapping',
    subtitle: '9 of 10 categories covered with 1 on roadmap',
    text: 'Every test in AccuGen maps to the OWASP LLM Top 10 framework — the industry standard for AI security governance. Each result includes severity rating, risk description, and actionable remediation guidance.',
    bullets: [
      'LLM01 – Prompt Injection (22 tests)',
      'LLM02 – Insecure Output Handling (18)',
      'LLM03 – Training Data Poisoning (15)',
      'LLM04–09 – Full coverage',
      'Severity: Critical / High / Medium / Low',
      'Remediation guidance per finding',
    ],
    tag: 'Compliance',
  },
  {
    icon: '/icons/features/features_23_cicd.svg',
    id: 'cicd',
    title: 'CI/CD Pipeline Integration',
    subtitle: 'Shift-left AI security into your dev workflow',
    text: 'AccuGen is built API-first so it integrates into any CI/CD system — GitHub Actions, GitLab CI, Jenkins, or custom pipelines. Automatically trigger full evaluation suites on every model update, prompt change, or RAG pipeline modification.',
    bullets: [
      'REST API with JSON responses',
      'Configurable pass/fail thresholds',
      'Webhook notifications on completion',
      'CSV test case upload for custom suites',
      'Parallel execution for speed',
      'WAL-mode SQLite for high concurrency',
    ],
    tag: 'DevOps',
  },
  {
    icon: '/icons/features/features_24_reports.svg',
    id: 'reports',
    title: 'Assessment Report Generation',
    subtitle: 'Audit-ready compliance reports in one click',
    text: 'Generate comprehensive OWASP-mapped PDF reports suitable for board presentations, regulatory filings, and internal audits. Interactive on-screen dashboards let you drill into individual failures and compare results across evaluation runs.',
    bullets: [
      'One-click PDF export',
      'OWASP LLM Top 10 mapping in report',
      'Severity distribution charts',
      'Score donut visualisations',
      'Collapsible failure categories',
      'Side-by-side run comparison',
    ],
    tag: 'Reporting',
  },
  {
    icon: '/icons/features/features_25_slack.svg',
    id: 'slack',
    title: 'Slack Notifications & Alerts',
    subtitle: 'Real-time evaluation results delivered to your team',
    text: 'Connect AccuGen to your Slack workspace and keep your entire team instantly informed. The moment an evaluation completes — or a critical security finding is detected — the right people get notified in the right channel, without leaving Slack.',
    bullets: [
      'Instant evaluation completion alerts',
      'Critical & high-severity vulnerability pings',
      'Configurable channel routing per project',
      'Score summary cards with pass / fail status',
      'Direct deep-link back to full report in AccuGen',
      'Mention specific team members on threshold breach',
      'Works with Slack free, Pro, Business+, and Enterprise Grid',
    ],
    tag: 'Integrations',
    //connectLabel: 'Connect Slack',
    //connectHref: '/contact?integration=slack',
    //connectNote: 'Requires a Slack workspace. Connect takes under 2 minutes via OAuth.',
  },
  {
    icon: '/icons/features/features_26_arize-phoenix.svg',
    id: 'arize',
    title: 'Arize Phoenix Export',
    subtitle: 'Push evaluation traces into your LLM observability stack',
    text: 'AccuGen integrates natively with Arize Phoenix — the open-source LLM observability platform. Export every evaluation run as structured OpenTelemetry traces so your team can monitor quality trends, debug failures, and correlate AccuGen security findings with production traffic in one unified view.',
    bullets: [
      'One-click export to Arize Phoenix projects',
      'OpenTelemetry-compatible trace format',
      'Quality scores attached as span attributes',
      'OWASP severity levels mapped to trace tags',
      'Correlate eval results with live production spans',
      'Supports self-hosted and Arize Cloud deployments',
      'Historical trend dashboards inside Phoenix UI',
    ],
    tag: 'Integrations',
    //connectLabel: 'Connect Arize Phoenix',
    //connectHref: '/contact?integration=arize-phoenix',
   // connectNote: 'Compatible with Arize Phoenix v4+. Works with both cloud and self-hosted deployments.',
  },
  {
    icon: '/icons/features/features_27_synthetic-data.svg',
    id: 'synthetic',
    title: 'Synthetic Ground Truth Generation',
    subtitle: 'Evaluation-ready datasets from your knowledge base — automatically',
    //badge: 'Coming Soon',
    text: 'Stop waiting for labeled data. AccuGen automatically generates high-quality ground-truth evaluation datasets from your own knowledge base — so you can measure AI accuracy from day one, without real user traffic. Most enterprise AI teams reach production without proper evaluation. They rely on manual spot checks, small hand-labeled datasets, or generic metrics that don\'t reflect their domain — leading to systems that fail silently under real user traffic.',
    bullets: [
      'Ground-truth Q&A pairs generated from any document, PDF, or wiki',
      'Query evolution: multi-hop, adversarial & paraphrased variants automatically',
      'Persona-based simulation across novice, expert & skeptical users',
      'Bias & balance audit — demographic, topic, and difficulty distribution checks',
      'Evaluate faithfulness, context recall, precision, and hallucination rate',
      'Privacy-safe by design — synthetic data contains zero PII from source docs',
      'GDPR, HIPAA, and enterprise data governance compliant from the ground up',
    ],
    tag: 'Coming Soon — Roadmap Feature',
    //connectLabel: 'Request Early Access',
    //connectHref: '/contact?feature=synthetic-ground-truth',
    //connectNote: 'Join the AccuGen early access program and get this feature before general availability — with dedicated onboarding support.',
  },
  {
    icon: '/icons/features/features_28_observability.svg',
    id: 'observability',
    title: 'Observability Integration',
    subtitle: 'One signal pipeline — from AI quality to your monitoring stack',
    //badge: 'Coming Soon',
    text: 'AccuGen connects your AI quality signals directly into your existing observability stack — giving your enterprise a single pane of glass for AI performance, safety, and compliance monitoring. AI quality metrics live in one place. Infrastructure metrics live in another. Security alerts live somewhere else entirely. AccuGen closes that gap with OpenTelemetry-native signals.',
    bullets: [
      'OpenTelemetry-native OTLP export — no vendor lock-in, any OTel backend',
      'Native connectors for Datadog, Grafana, Splunk, Prometheus, New Relic, PagerDuty',
      'Pre-built dashboard templates for GenAI quality monitoring — deploy in minutes',
      'Intelligent alerting: route AI quality degradation to PagerDuty or Slack on-call',
      'Drift detection: catch slow quality regressions before they become incidents',
      'Immutable compliance audit trail — full provenance for every evaluation run',
      'Real-time continuous evaluation on live production traffic — not periodic batch jobs',
    ],
    tag: 'Coming Soon — Roadmap Feature',
    //connectLabel: 'Request Early Access',
    //connectHref: '/contact?feature=observability',
    //connectNote: 'Be among the first enterprises to run production AI monitoring with native observability integration.',
  },
];

/* ─── Synthetic data extra content ─── */
const synthPipeline = [
  { n: '1', label: 'Connect Knowledge Base', desc: 'Docs, PDFs, wikis, or APIs' },
  { n: '2', label: 'Chunk & Index',           desc: 'Production-matched chunking strategies' },
  { n: '3', label: 'Synthetic Generation',    desc: 'Questions + gold-standard answers per chunk', active: true },
  { n: '4', label: 'Evolve & Diversify',      desc: 'Edge cases, personas, adversarial variants' },
  { n: '5', label: 'Evaluate & Score',        desc: 'Faithfulness, recall, precision, accuracy' },
];
const synthStats = [
  { num: '10×',   label: 'faster to evaluation-ready datasets vs manual labeling' },
  { num: 'Zero',  label: 'PII exposure — synthetic data is privacy-safe by design' },
  { num: '100%',  label: 'domain-grounded — generated from your content, not generic benchmarks' },
  { num: 'Day 1', label: 'evaluation coverage — no real user traffic required' },
];
const synthTable = [
  { artifact: 'Synthetic Question',   desc: 'Realistic user query generated from a specific document chunk — grounded in your domain language and intent patterns',            role: 'Retrieval testing' },
  { artifact: 'Gold-Standard Answer', desc: 'Correct answer derived strictly from the source chunk — serves as the reference output for generation evaluation',               role: 'Generation accuracy' },
  { artifact: 'Ground-Truth Context', desc: 'The exact chunk used as the reference — enabling isolation of retrieval vs generation failures',                                  role: 'Context recall' },
  { artifact: 'Evolved Variants',     desc: 'Multi-hop, adversarial, paraphrased, and persona-adapted versions of the base question',                                         role: 'Robustness testing' },
  { artifact: 'Difficulty Labels',    desc: 'Simple, reasoning-required, and multi-document question tiers for stratified evaluation benchmarks',                              role: 'Benchmark stratification' },
  { artifact: 'Dataset Metadata',     desc: 'Source chunk ID, topic distribution, and bias indicators — enabling dataset quality audits before use',                          role: 'Governance & audit' },
];

/* ─── Observability extra content ─── */
const obsStats = [
  { num: '1',         label: 'unified dashboard for AI quality, security, and infrastructure health' },
  { num: 'OTel',      label: 'OpenTelemetry-native — no vendor lock-in, works with any backend' },
  { num: '<5 min',    label: 'to first quality signal in your existing observability platform' },
  { num: 'Real-time', label: 'continuous evaluation on live production traffic — not periodic batch jobs' },
];
const obsIntegrations = [
  { abbr: 'DD', color: '#632ca6', name: 'Datadog LLM Observability',  desc: 'Native connector — quality scores, hallucination rates, and OWASP signals flow into Datadog traces and monitors' },
  { abbr: 'Gr', color: '#f46800', name: 'Grafana Cloud',               desc: 'Pre-built AccuGen dashboards for GenAI evaluation metrics, alert rules, and SLO tracking via OTLP' },
  { abbr: 'Sp', color: '#1a1a1a', name: 'Splunk',                      desc: 'AI evaluation events as structured logs — searchable, alertable, and compliance-reportable in Splunk' },
  { abbr: 'Pr', color: '#e6522c', name: 'Prometheus + Alertmanager',   desc: 'AccuGen quality metrics exposed as Prometheus endpoints — scrape and alert on AI quality just like infra' },
  { abbr: 'NR', color: '#009bde', name: 'New Relic',                   desc: 'Forward AI quality telemetry via New Relic OTLP ingest — unified AI + APM dashboards in one view' },
  { abbr: 'PD', color: '#06ac38', name: 'PagerDuty',                   desc: 'Route AI quality degradation alerts directly to your on-call rotation — with full evaluation context attached' },
];
const obsMetrics = [
  { signal: 'Hallucination Rate',      desc: 'Percentage of responses containing fabricated facts — tracked over time and by model version',                                    cat: 'Quality',    cls: 'fp-cat-quality' },
  { signal: 'Faithfulness Score',      desc: 'How closely generated answers are grounded in retrieved context — key for RAG pipeline health',                                   cat: 'Quality',    cls: 'fp-cat-quality' },
  { signal: 'Context Recall',          desc: 'Whether the retriever surfaces all information needed to answer — isolation of retrieval failures',                                cat: 'Quality',    cls: 'fp-cat-quality' },
  { signal: 'OWASP Violation Rate',    desc: 'Frequency of prompt injection, jailbreak, or sensitive data exposure attempts detected in production',                            cat: 'Security',   cls: 'fp-cat-security' },
  { signal: 'Bias & Toxicity Index',   desc: 'Rolling measurement of response fairness and harmful content across user demographics',                                           cat: 'Security',   cls: 'fp-cat-security' },
  { signal: 'Response Accuracy Drift', desc: 'Trend detection when AI answer quality degrades relative to ground-truth baselines over time',                                    cat: 'Operations', cls: 'fp-cat-ops' },
  { signal: 'Evaluation SLO Status',   desc: 'Service-level objective tracking for AI quality — breach alerts, trend analysis, and historical burn rates',                      cat: 'Operations', cls: 'fp-cat-ops' },
  { signal: 'Compliance Audit Events', desc: 'Immutable log of every evaluation run — supporting governance, regulatory reporting, and change control',                         cat: 'Operations', cls: 'fp-cat-ops' },
];

export default function FeaturesPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); }
      }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="Features"
        description="Explore AccuGen's full feature set: AI quality scoring, security vulnerability testing, bias detection, OWASP LLM Top 10 mapping, CI/CD integration, Slack notifications, Arize Phoenix export, synthetic ground truth generation, and observability integration."
        canonical="/features"
        keywords="AI quality scoring, LLM security testing, bias detection, OWASP mapping, CI/CD AI integration, synthetic ground truth, LLM observability, OpenTelemetry AI monitoring"
        schema={schemas.breadcrumb([{ name: 'Home', path: '/' }, { name: 'Features', path: '/features' }])}
      />
      <Header />

      <PageHero
        eyebrow="Core Capabilities"
        title="Everything You Need to<br/>Validate Enterprise <em>AI</em>"
        subtitle="From response accuracy to security vulnerabilities, AccuGen provides comprehensive evaluation across every dimension that matters for enterprise AI governance."
        crumb="Features"
      />

      {features.map((f, i) => (
        <React.Fragment key={f.id}>

          {/* ── Main feature section (alternating) ── */}
          <section
            id={f.id}
            className={`ip-section${i % 2 === 1 ? ' ip-bg-lav' : ''}`}
          >
            <div className="container">
              <div className={`ip-split${i % 2 === 1 ? ' ip-split-rev' : ''}`}>

                <div>
                  <div className="fp-tag-row reveal">
                    <span className="ip-feat-tag">{f.tag}</span>
                    {f.badge && <span className="fp-badge-roadmap">{f.badge}</span>}
                  </div>
                  <h2 className="section-h reveal d1">{f.title}</h2>
                  <p className="ip-feat-subtitle reveal d1">{f.subtitle}</p>
                  <p className="ip-body reveal d2">{f.text}</p>
                  <div className="ip-check-list reveal d3">
                    {f.bullets.map(b => (
                      <div key={b} className="ip-check-item">
                        <span className="ip-check-icon">✓</span>
                        <span>{b}</span>
                      </div>
                    ))}
                  </div>
                  {f.connectLabel && (
                    <div className="ip-connect-cta reveal d4">
                      <a href={f.connectHref} className="btn-navy">
                        {f.connectLabel} →
                      </a>
                      {f.connectNote && (
                        <p className="ip-connect-note">{f.connectNote}</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="ip-feat-visual reveal d2">
                  <div className="ifv-icon">
                    <img src={f.icon} alt={f.title} />
                  </div>
                  <div className="ifv-title">{f.title}</div>
                  <div className="ifv-tag">{f.tag}</div>
                </div>

              </div>

            </div>
          </section>

          {/* ── Synthetic Ground Truth extra panels ── */}
          {f.id === 'synthetic' && (
            <>
              <section className="ip-section ip-bg-lav">
                <div className="container">
                  <p className="eyebrow reveal" style={{ textAlign: 'center' }}>How It Works</p>
                  <h3 className="section-h reveal d1" style={{ textAlign: 'center', marginBottom: 36 }}>
                    From your knowledge base to trusted ground truth — automatically
                  </h3>
                  <div className="fp-pipeline reveal d2">
                    {synthPipeline.map(s => (
                      <div key={s.n} className={`fp-pipeline-step${s.active ? ' fp-step-active' : ''}`}>
                        <div className="fp-step-num">{s.n}</div>
                        <div className="fp-step-label">{s.label}</div>
                        <div className="fp-step-desc">{s.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div className="fp-stats-row reveal d3">
                    {synthStats.map(s => (
                      <div key={s.num} className="fp-stat">
                        <div className="fp-stat-num">{s.num}</div>
                        <div className="fp-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="ip-section">
                <div className="container">
                  <p className="eyebrow reveal" style={{ textAlign: 'center' }}>Technical Details</p>
                  <h3 className="section-h reveal d1" style={{ textAlign: 'center', marginBottom: 8 }}>
                    What AccuGen generates and evaluates
                  </h3>
                  <p className="section-sub reveal d2" style={{ margin: '0 auto 36px', textAlign: 'center' }}>
                    Every synthetic dataset includes the artifacts your evaluation pipeline needs — not just questions.
                  </p>
                  <div className="fp-table-wrap reveal d3">
                    <table className="fp-table">
                      <thead>
                        <tr><th>Dataset Artifact</th><th>Description</th><th>Evaluation Role</th></tr>
                      </thead>
                      <tbody>
                        {synthTable.map(row => (
                          <tr key={row.artifact}>
                            <td className="fp-td-strong">{row.artifact}</td>
                            <td>{row.desc}</td>
                            <td><span className="fp-badge-teal">{row.role}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </>
          )}

          {/* ── Observability extra panels ── */}
          {f.id === 'observability' && (
            <>
              <section className="ip-section ip-bg-lav">
                <div className="container">
                  <p className="eyebrow reveal" style={{ textAlign: 'center' }}>Architecture</p>
                  <h3 className="section-h reveal d1" style={{ textAlign: 'center', marginBottom: 8 }}>
                    One signal pipeline — from AI quality to your observability stack
                  </h3>
                  <p className="section-sub reveal d2" style={{ margin: '0 auto 36px', textAlign: 'center' }}>
                    AccuGen emits evaluation telemetry using the OpenTelemetry standard, so quality, safety,
                    and compliance signals flow directly into the tools your enterprise already uses.
                  </p>
                  <div className="fp-stats-row reveal d3">
                    {obsStats.map(s => (
                      <div key={s.num} className="fp-stat">
                        <div className="fp-stat-num">{s.num}</div>
                        <div className="fp-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <p className="eyebrow reveal" style={{ textAlign: 'center', marginBottom: 20 }}>Supported Platforms</p>
                  <div className="fp-integrations reveal d2">
                    {obsIntegrations.map(intg => (
                      <div key={intg.name} className="fp-intg-card">
                        <div className="fp-intg-logo" style={{ background: intg.color }}>{intg.abbr}</div>
                        <div>
                          <h4>{intg.name}</h4>
                          <p>{intg.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="ip-section">
                <div className="container">
                  <p className="eyebrow reveal" style={{ textAlign: 'center' }}>What AccuGen Monitors</p>
                  <h3 className="section-h reveal d1" style={{ textAlign: 'center', marginBottom: 8 }}>
                    Every signal your enterprise needs — not just latency and tokens
                  </h3>
                  <p className="section-sub reveal d2" style={{ margin: '0 auto 36px', textAlign: 'center' }}>
                    AccuGen enriches your observability stack with AI-specific signals that infrastructure monitoring tools can't produce on their own.
                  </p>
                  <div className="fp-table-wrap reveal d3">
                    <table className="fp-table">
                      <thead>
                        <tr><th>Signal</th><th>What it measures</th><th>Category</th></tr>
                      </thead>
                      <tbody>
                        {obsMetrics.map(row => (
                          <tr key={row.signal}>
                            <td className="fp-td-strong">{row.signal}</td>
                            <td>{row.desc}</td>
                            <td><span className={`fp-badge-cat ${row.cls}`}>{row.cat}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="fp-audience-grid reveal d4">
                    <div className="fp-audience-card">
                      <h4>For Platform Engineering Teams</h4>
                      <p>Treat AI quality as a first-class service health metric. Define SLOs on hallucination rate and faithfulness scores, alert on breaches, and include AI quality in your weekly reliability reviews — alongside latency and error rate.</p>
                    </div>
                    <div className="fp-audience-card">
                      <h4>For Security & Compliance Teams</h4>
                      <p>Continuous OWASP LLM compliance monitoring in production — not periodic audits. Capture every violation event with full context for regulatory reporting, risk registers, and third-party audits.</p>
                    </div>
                    <div className="fp-audience-card">
                      <h4>For Executive & Business Stakeholders</h4>
                      <p>Track AI quality as a business KPI. AccuGen feeds Grafana or Datadog dashboards your leadership already reviews — making AI accuracy, safety, and compliance visible at the board level.</p>
                    </div>
                  </div>
                </div>
              </section>
            </>
          )}

        </React.Fragment>
      ))}

      {/* ── CTA band ── */}
      <section className="ip-cta-band">
        <div className="container">
          <h2>See Every Feature in Action</h2>
          <p>Launch the platform and run your first evaluation in minutes.</p>
          <div className="ip-cta-btns">
            <a href="http://diq.eprodevops.com:3500" className="btn-navy" target="_blank" rel="noreferrer">
              Launch Platform →
            </a>
            <Link to="/contact" className="btn-outline-navy">Talk to Sales</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
